import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Heart } from "lucide-react";

const LOGO = `${process.env.PUBLIC_URL}/logo.png`;

const primary = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/service-times", label: "Service Times" },
  { to: "/calendar", label: "Calendar" },
  { to: "/gallery", label: "Gallery" },
];

const more = [
  { to: "/transport", label: "Free Transport" },
  { to: "/parking", label: "Free Parking" },
  { to: "/childrens-church", label: "Children's Church" },
  { to: "/kingdom-care", label: "Kingdom Care Giving" },
  { to: "/#give", label: "Giving" },
  { to: "/wsf", label: "WSF Resources" },
  { to: "/testimonies", label: "Testimonies" },
  { to: "/covenant-hour", label: "Covenant Hour of Prayer" },
  { to: "/contact", label: "Visit Us" },
];

const allLinks = [...primary, ...more];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 inset-x-0 z-50 glass-strong border-b border-white/10 transition-all duration-300 ${
        scrolled ? "py-2 shadow-[0_6px_24px_rgba(0,0,0,0.07)]" : "py-3.5"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <img
            src={LOGO}
            alt="Winners Chapel International logo"
            className={`object-contain transition-all duration-300 ${scrolled ? "h-11" : "h-14"}`}
          />
          <span className="leading-tight">
            <span className="block font-heading font-extrabold text-white text-base sm:text-lg tracking-tight">
              Winners Chapel
            </span>
            <span className="block text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.28em] text-chapel-red">
              International Colchester
            </span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {primary.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-semibold rounded-full transition-colors duration-200 ${
                  isActive ? "text-chapel-red bg-chapel-red/10" : "text-zinc-200 hover:text-chapel-red"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}

          {/* More dropdown */}
          <div className="relative" ref={moreRef}>
            <button
              onClick={() => setMoreOpen((v) => !v)}
              data-testid="nav-more-button"
              className={`flex items-center gap-1 px-3.5 py-2 text-sm font-semibold rounded-full transition-colors ${
                moreOpen ? "text-chapel-red bg-chapel-red/10" : "text-zinc-200 hover:text-chapel-red"
              }`}
            >
              More <ChevronDown className={`w-4 h-4 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {moreOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.97 }}
                  transition={{ duration: 0.18 }}
                  data-testid="nav-more-dropdown"
                  className="absolute right-0 mt-2 w-60 bg-sec-1 rounded-2xl border border-white/10 shadow-[0_16px_44px_rgba(0,0,0,0.5)] p-2"
                >
                  {allLinks.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      onClick={() => setMoreOpen(false)}
                      data-testid={`more-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
                      className={({ isActive }) =>
                        `block px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                          isActive ? "text-chapel-red bg-chapel-red/10" : "text-zinc-200 hover:bg-white/10 hover:text-chapel-red"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link
            to="/contact"
            data-testid="navbar-plan-visit"
            className="ml-3 bg-chapel-red hover:bg-chapel-glow text-white text-sm font-semibold rounded-full px-6 py-2.5 transition-all duration-300 red-glow"
          >
            Plan Your Visit
          </Link>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-white"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-sec-1 border-t border-white/10 overflow-hidden max-h-[70vh] overflow-y-auto"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {allLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                      isActive ? "text-chapel-red bg-chapel-red/10" : "text-zinc-200"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="mt-2 text-center bg-chapel-red text-white font-semibold rounded-xl px-6 py-3"
              >
                Plan Your Visit
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
